import dayjs from "dayjs"

import SubscriptionPlanTitles from "constants/SubscriptionPlanTitles"
import { ApiTypes } from "types"
import SubscriptionPlan from "types/enums/SubscriptionPlan"

import type { PartnerAnalytics } from "components/common/MonthlyEngagementPage/PartnerLevelAnalytics/types"

import {
  callsSection,
  DEFAULT_REPORTING_PERIOD,
  executiveMetrics,
  growthMetrics,
  mobileKpiGroups,
  mobileWeeklyTrend,
  thermostatKpis,
  thermostatWeeklyTrend,
  topDevices,
} from "./data"
import type {
  CallsSection,
  CustomerCallLog,
  CustomerCallsSection,
  EngagementKpiMetric,
  EngagementPeriodLabels,
  ExecutiveMetric,
  GrowthMetric,
  MobileWeeklyTrend,
  ThermostatWeeklyTrend,
  TopDevice,
} from "./types"

export interface MonthlyEngagementReportData {
  contractorName: string
  planLabel: string
  activeDevices: string
  activatedMobileApps: string
  reportingPeriod: string
  executiveMetrics: ExecutiveMetric[]
  growthMetrics: GrowthMetric[]
  thermostatKpis: EngagementKpiMetric[]
  thermostatWeeklyTrend: ThermostatWeeklyTrend[]
  topDevices: TopDevice[]
  mobileKpiGroups: Array<{ title: string; metrics: EngagementKpiMetric[] }>
  mobileWeeklyTrend: MobileWeeklyTrend[]
  calls: CallsSection
  customerCalls: CustomerCallsSection
  partnerAnalytics: PartnerAnalytics[]
  periodLabels: EngagementPeriodLabels
}

export type EngagementPeriodType = "week-to-date" | "month-to-date" | "custom"

export const getEngagementPeriodLabels = ({
  from,
  to,
  type,
}: {
  from?: string
  to?: string
  type?: string
}): EngagementPeriodLabels => {
  if (type === "week-to-date") return { previous: "last week", past: "past week" }
  if (type === "month-to-date") return { previous: "last month", past: "past month" }

  const start = dayjs(from)
  const end = dayjs(to)
  const days = start.isValid() && end.isValid() ? Math.max(1, end.diff(start, "day") + 1) : 0

  if (days > 0) return { previous: `last ${days} days`, past: `past ${days} days` }

  return { previous: "last month", past: "past month" }
}

const valueOrFallback = (value: number | null | undefined, fallback: number) =>
  typeof value === "number" && Number.isFinite(value) ? value : fallback
const formatNumber = (value: number | null | undefined, fallback: number) =>
  valueOrFallback(value, fallback).toLocaleString()

const parseSparkline = (sparkline?: string | number[] | null): number[] => {
  if (Array.isArray(sparkline)) {
    return sparkline.filter((value): value is number => typeof value === "number" && Number.isFinite(value))
  }

  if (typeof sparkline !== "string" || !sparkline.trim()) return []

  try {
    const parsed = JSON.parse(sparkline) as unknown

    if (Array.isArray(parsed)) {
      return parsed.filter((value): value is number => typeof value === "number" && Number.isFinite(value))
    }
  } catch {
    // Fall back to comma-separated parsing below.
  }

  return sparkline
    .split(",")
    .map(value => Number(value.trim()))
    .filter(value => Number.isFinite(value))
}

const formatPlanLabel = (plan?: SubscriptionPlan | string | null) => {
  if (!plan) return ""

  if (plan in SubscriptionPlanTitles) {
    return `${SubscriptionPlanTitles[plan as SubscriptionPlan]} Plan`
  }

  return plan.includes("Plan") ? plan : `${plan} Plan`
}

const createDefaultExecutiveMetric = (title: ExecutiveMetric["title"]): ExecutiveMetric => ({
  title,
  value: 0,
  percent: 0,
  direction: "flat",
  sparkline: [],
})

const createDefaultGrowthMetric = (title: GrowthMetric["title"]): GrowthMetric => ({
  title,
  value: 0,
  direction: "flat",
  total: 0,
})

const createDefaultKpiMetric = (
  metricMetadata: Pick<EngagementKpiMetric, "id" | "title" | "subtitle">,
): EngagementKpiMetric => ({
  ...metricMetadata,
  value: 0,
  direction: "flat",
  sparkline: [],
  percent_change: 0,
})

const createDefaultCallsMetric = (
  metricMetadata: Pick<CallsSection["metrics"][number], "id" | "title" | "sourceLabel">,
): CallsSection["metrics"][number] => ({
  ...metricMetadata,
  value: metricMetadata.id === "avg-talk-time" ? "0m 0s" : "0",
  direction: "flat",
  percent_change: 0,
})

const buildExecutiveMetrics = (summary?: ApiTypes.Res.MonthlyEngagementSummary): ExecutiveMetric[] => {
  const overview = summary?.engagement_overview

  const metricSources: Record<
    string,
    | {
        value?: number | null
        percent_change?: number | null
        trend_direction?: string | null
        sparkline?: string | number[] | null
      }
    | undefined
  > = {
    "Total Engagement Actions": overview?.total_engagement_actions,
    "Thermostat Engagement": overview?.thermostat_engagement,
    "Mobile App Engagement": overview?.mobile_app_engagement,
    "Total Conversions": overview?.total_conversions,
  }

  return executiveMetrics.map(metricMetadata => {
    const metric = createDefaultExecutiveMetric(metricMetadata.title)
    const source = metricSources[metric.title]

    if (!source) return metric

    return {
      ...metric,
      value: valueOrFallback(source.value, metric.value),
      percent: valueOrFallback(source.percent_change, 0),
      direction: (source.trend_direction ?? metric.direction) as ExecutiveMetric["direction"],
      sparkline: parseSparkline(source.sparkline),
    }
  })
}

const buildGrowthMetrics = (summary?: ApiTypes.Res.MonthlyEngagementSummary): GrowthMetric[] => {
  const growth = summary?.header_summary?.growth

  const growthSources: Record<
    string,
    { value?: number | null; trend_direction?: string | null; total?: number | null } | undefined
  > = {
    Customers: growth?.customers,
    Thermostats: growth?.therostats,
    "App Installs": growth?.app_installs,
  }

  return growthMetrics.map(metricMetadata => {
    const metric = createDefaultGrowthMetric(metricMetadata.title)
    const source = growthSources[metric.title]

    if (!source) return metric

    return {
      ...metric,
      value: source.value ?? metric.value,
      direction: (source.trend_direction ?? metric.direction) as GrowthMetric["direction"],
      total: source.total ?? metric.total,
    }
  })
}

const buildThermostatKpis = (thermostatEngagement?: ApiTypes.Res.IThermostatEngagement): EngagementKpiMetric[] => {
  type ThermostatKpiSource = {
    value?: number
    trend_direction?: string
    percent_change?: number
    sparkline?: string | number[]
  }

  const thermostatSources: Record<string, ThermostatKpiSource | undefined> = {
    clicks: thermostatEngagement?.clicks_on_logo,
    "qr-scans": thermostatEngagement?.qr_scans,
  }

  return thermostatKpis.map(metricMetadata => {
    const metric = createDefaultKpiMetric(metricMetadata)
    const source = thermostatSources[metric.id]

    if (!source) return metric

    return {
      ...metric,
      value: source.value ?? metric.value,
      direction: (source.trend_direction ?? metric.direction) as EngagementKpiMetric["direction"],
      percent_change: source.percent_change ?? metric.percent_change ?? 0,
      sparkline: parseSparkline(source.sparkline),
    }
  })
}

const buildMobileKpiGroups = (mobileAppEngagement?: ApiTypes.Res.IMobileAppEngagement) => {
  type MobileKpiSource = {
    value?: number
    trend_direction?: string
    percent_change?: number
    sparkline?: string | number[]
  }

  const mobileSources: Record<string, MobileKpiSource | undefined> = {
    "mobile-clicks": mobileAppEngagement?.navigation_actions?.clicks_on_logo_app,
    "support-clicks": mobileAppEngagement?.navigation_actions?.support_tab_clicks,
    "website-actions": mobileAppEngagement?.website_actions?.website_actions,
    "website-conversion": mobileAppEngagement?.website_actions?.continue_to_website_conversion,
    "technician-clicks": mobileAppEngagement?.technician_requests?.request_technician_clicks,
    "technician-conversion": mobileAppEngagement?.technician_requests?.continue_to_request_conversion,
    "app-calls": mobileAppEngagement?.direct_contact_actions?.app_calls,
    "app-texts": mobileAppEngagement?.direct_contact_actions?.app_texts,
  }

  return mobileKpiGroups.map(group => ({
    ...group,
    metrics: group.metrics.map(metricMetadata => {
      const metric = createDefaultKpiMetric(metricMetadata)
      const source = mobileSources[metric.id]

      if (!source) return metric

      return {
        ...metric,
        value: valueOrFallback(source.value, metric.value),
        direction: (source.trend_direction ?? metric.direction) as EngagementKpiMetric["direction"],
        percent_change: source.percent_change ?? metric.percent_change ?? 0,
        sparkline: parseSparkline(source.sparkline),
      }
    }),
  }))
}

const buildThermostatWeeklyTrend = (
  thermostatEngagement?: ApiTypes.Res.IThermostatEngagement,
): ThermostatWeeklyTrend[] =>
  thermostatEngagement?.weekly_trend?.length
    ? thermostatEngagement.weekly_trend.map(item => ({
        week: item.week,
        clicks: item.clicks_on_logo ?? 0,
        scans: item.qr_scans ?? 0,
      }))
    : thermostatWeeklyTrend

const buildMobileWeeklyTrend = (mobileAppEngagement?: ApiTypes.Res.IMobileAppEngagement): MobileWeeklyTrend[] =>
  mobileAppEngagement?.weekly_trend?.length
    ? mobileAppEngagement.weekly_trend.map(item => ({
        week: item.week,
        navigation: item.navigation ?? 0,
        requests: item.requests ?? 0,
        contact: item.contact ?? 0,
        website: item.website ?? 0,
      }))
    : mobileWeeklyTrend

const buildCallsSection = (calls?: ApiTypes.Res.ICallsEngagement): CallsSection => {
  const callsTaken = calls?.calls_taken
  const answeredByHuman = calls?.answered_by_a_human
  const avgTalkTime = calls?.avg_talk_time

  const metricSources: Record<string, ApiTypes.Res.CallsEngagementMetric | undefined> = {
    "calls-taken": callsTaken,
    connected: calls?.connected,
    "answered-by-human": answeredByHuman,
    "avg-talk-time": avgTalkTime,
  }

  const callsTakenValue = valueOrFallback(Number(callsTaken?.value), 0)
  const answeredByHumanValue = valueOrFallback(Number(answeredByHuman?.value), 0)
  const missedCalls = Math.max(callsTakenValue - answeredByHumanValue, 0)

  const formatMetricValue = (value: number | string | null | undefined, fallback: string) => {
    if (typeof value === "string" && value.trim()) return value
    if (typeof value === "number" && Number.isFinite(value)) return value.toLocaleString()

    return fallback
  }

  const formatDuration = (value: number | string | null | undefined, fallback: string) => {
    if (typeof value === "string" && value.trim()) return value
    if (typeof value !== "number" || !Number.isFinite(value)) return fallback

    const minutes = Math.floor(value / 60)
    const seconds = Math.round(value % 60)

    return `${minutes}m ${seconds}s`
  }

  return {
    metrics: callsSection.metrics.map(metricMetadata => {
      const metric = createDefaultCallsMetric(metricMetadata)
      const source = metricSources[metric.id]
      const value =
        metric.id === "avg-talk-time"
          ? formatDuration(source?.value, metric.value)
          : formatMetricValue(source?.value, metric.value)

      return {
        ...metric,
        value,
        direction: (source?.trend_direction ?? metric.direction) as EngagementKpiMetric["direction"],
        percent_change: source?.percent_change ?? metric.percent_change ?? 0,
      }
    }),
    summary:
      calls?.summary ??
      `Your customers placed ${callsTakenValue.toLocaleString()} calls: ` +
        `${answeredByHumanValue.toLocaleString()} were answered, while ` +
        `${missedCalls.toLocaleString()} were missed or went to voicemail.`,
  }
}

const formatCallDuration = (value: number | null | undefined, fallback = "—") => {
  if (typeof value !== "number" || !Number.isFinite(value)) return fallback
  if (value < 60) return `${Math.round(value)}s`

  return `${Math.floor(value / 60)}m ${String(Math.round(value % 60)).padStart(2, "0")}s`
}

const formatCallTimestamp = (timestamp: string) => {
  const date = new Date(timestamp)
  if (Number.isNaN(date.getTime())) return timestamp

  return date.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
}

const formatTimelineTime = (start: string, timestamp: string) => {
  const elapsedSeconds = Math.max(0, Math.round((new Date(timestamp).getTime() - new Date(start).getTime()) / 1000))
  const minutes = Math.floor(elapsedSeconds / 60)
  const seconds = elapsedSeconds % 60

  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
}

const getTimelineTitle = (eventType: string) =>
  eventType
    .split("_")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ")

const getHeatmapBuckets = (hours: number[] | undefined) =>
  Array.from({ length: 8 }, (_, bucket) =>
    (hours ?? []).slice(bucket * 3, bucket * 3 + 3).reduce((sum, value) => sum + value, 0),
  )

const buildCustomerCallsSection = (
  calls: ApiTypes.Res.ICallsEngagement | undefined,
  outcomes: ApiTypes.Res.ICallOutcomesReport | undefined,
  log: ApiTypes.Res.ICallLogReport | undefined,
): CustomerCallsSection => {
  const legacy = buildCallsSection(calls)
  const callOutcomes = outcomes?.call_outcomes
  const callLogs: CustomerCallLog[] = (log?.call_log ?? []).map(call => ({
    id: call.call_id,
    timestamp: formatCallTimestamp(call.occurred_at),
    customerName: call.customer_name ?? "Unknown",
    tradeLine: "—",
    phone: call.calling_number,
    outcome: call.outcome,
    ringTime: formatCallDuration(call.ring_time_seconds),
    talkTime: formatCallDuration(call.talk_time_seconds),
    source: "Twilio",
    timeline: (call.timeline ?? []).map(event => ({
      time: formatTimelineTime(call.occurred_at, event.timestamp),
      title: getTimelineTitle(event.event_type),
      description: "",
    })),
  }))
  const heatmap = calls?.calls_by_day_and_hour
  const heatmapDays = [
    ["Mon", heatmap?.monday],
    ["Tue", heatmap?.tuesday],
    ["Wed", heatmap?.wednesday],
    ["Thu", heatmap?.thursday],
    ["Fri", heatmap?.friday],
    ["Sat", heatmap?.saturday],
    ["Sun", heatmap?.sunday],
  ] as const

  return {
    ...legacy,
    summary: callOutcomes
      ? `Your customers placed ${callOutcomes.total_calls.toLocaleString()} calls: ` +
        `${callOutcomes.answered_calls.toLocaleString()} were answered, while ` +
        `${callOutcomes.unanswered_calls.toLocaleString()} were missed or went to voicemail.`
      : legacy.summary,
    callLogs,
    callsByDayAndHour: heatmap ? heatmapDays.map(([day, hours]) => ({ day, hours: getHeatmapBuckets(hours) })) : [],
    heatmapTotal: heatmap?.total ?? 0,
  }
}

const buildTopDevices = (thermostatEngagement?: ApiTypes.Res.IThermostatEngagement): TopDevice[] =>
  thermostatEngagement?.top_engaged_devices?.length
    ? thermostatEngagement.top_engaged_devices.map(device => ({
        deviceId: device.device_id ?? "",
        clicks: device.clicks_on_logo ?? 0,
        scans: device.qr_scans ?? 0,
        isTop: device.is_top_device,
      }))
    : topDevices

export const buildPartnerAnalytics = (
  partnerLevelAnalytics?: ApiTypes.Res.IPartnerLevelAnalytics,
): PartnerAnalytics[] =>
  partnerLevelAnalytics?.partners?.length
    ? partnerLevelAnalytics.partners.map(partner => ({
        name: partner.partner_category,
        source: partner.partner_website_url ?? "Partner website URL",
        thermostatLogoClicks: partner.thermostat_device.logo_clicks ?? 0,
        thermostatQrScans: partner.thermostat_device.qr_scans ?? 0,
        mobileLogoClicks: partner.mobile_app.logo_clicks ?? 0,
        supportTabClicks: partner.mobile_app.support_tab ?? 0,
        websiteVisits: partner.mobile_app.website_visits ?? 0,
        requestTechClicks: partner.mobile_app.request_tech ?? 0,
        callTextActions: partner.mobile_app.call_text ?? 0,
      }))
    : []

export const buildMonthlyEngagementReportData = ({
  summary,
  customerCalls,
  reportingPeriodFrom,
  reportingPeriodTo,
  reportingPeriodType,
}: {
  summary?: ApiTypes.Res.MonthlyEngagementReportResponse
  customerCalls?: {
    call_outcomes?: ApiTypes.Res.ICallOutcomesReport
    call_log?: ApiTypes.Res.ICallLogReport
  }
  reportingPeriodFrom?: string
  reportingPeriodTo?: string
  reportingPeriodType?: string
}): MonthlyEngagementReportData => {
  const engagementSummary = summary?.engagement_summary
  const partnerLevelAnalytics = summary?.partner_level_analytics?.partner_level_analytics

  return {
    contractorName: engagementSummary?.contractor?.name || "",
    planLabel: formatPlanLabel(engagementSummary?.contractor?.plan),
    activeDevices: formatNumber(engagementSummary?.summary?.active_devices, 0),
    activatedMobileApps: formatNumber(engagementSummary?.summary?.activated_mobile_apps, 0),
    reportingPeriod: engagementSummary?.reporting_period?.display_label || DEFAULT_REPORTING_PERIOD,
    executiveMetrics: buildExecutiveMetrics(engagementSummary),
    growthMetrics: buildGrowthMetrics(engagementSummary),
    thermostatKpis: buildThermostatKpis(summary?.thermostat_engagement?.thermostat_engagement),
    thermostatWeeklyTrend: buildThermostatWeeklyTrend(summary?.thermostat_engagement?.thermostat_engagement),
    topDevices: buildTopDevices(summary?.thermostat_engagement?.thermostat_engagement),
    mobileKpiGroups: buildMobileKpiGroups(summary?.mobile_app_engagement?.mobile_app_engagement),
    mobileWeeklyTrend: buildMobileWeeklyTrend(summary?.mobile_app_engagement?.mobile_app_engagement),
    calls: buildCallsSection(summary?.calls),
    customerCalls: buildCustomerCallsSection(summary?.calls, customerCalls?.call_outcomes, customerCalls?.call_log),
    partnerAnalytics: buildPartnerAnalytics(partnerLevelAnalytics),
    periodLabels: getEngagementPeriodLabels({
      from: reportingPeriodFrom,
      to: reportingPeriodTo,
      type: reportingPeriodType,
    }),
  }
}
